import React, { useEffect } from 'react';
import { Dispatch, SetStateAction } from 'react';
import Button from '../../commons/Button/Button';
import { Dropdown } from '../../commons/Dropdown/Dropdown';
import TextInput from '../../commons/TextInput/TextInput';

// Use a more specific constraint that allows string indexing
interface FiltersComponentProps<T extends Record<string, any>> {
  filtersCount: T;
  setFiltersCount: Dispatch<SetStateAction<T>>;
  filterOptions: { value: string; label: string }[];
  onSearch?: () => void;
  autoAddFilter?: boolean;
}

export const FiltersComponent = <T extends Record<string, any>>({
  filtersCount,
  setFiltersCount,
  filterOptions,
  onSearch,
  autoAddFilter = true,
}: FiltersComponentProps<T>) => {
  useEffect(() => {
    if (autoAddFilter && Object.keys(filtersCount).length === 0) {
      addFilter();
    }
  }, []);

  const addFilter = () => {
    const newFilter = filterOptions.find(
      (option) => !Object.keys(filtersCount).includes(option.value)
    );
    if (newFilter) {
      setFiltersCount((prevFilters) => {
        const newFilters = { ...prevFilters } as Record<string, any>;
        newFilters[newFilter.value] = '';
        return newFilters as T;
      });
    }
  };

  return (
    <div className=" mb-20 flex gap-20">
      <Button
        type="secondary"
        border
        onClick={addFilter}
        style={{
          padding: '0 1.6rem',
          whiteSpace: 'nowrap',
        }}
      >
        Add Filter
      </Button>
      <div className="flex flex-wrap gap-10">
        {Object.keys(filtersCount).map((filter: string, index: number) => {
          const value = filtersCount[filter as keyof T];
          return (
            <div key={index} className="flex gap-7">
              <Dropdown
                options={filterOptions.filter(
                  (option) => !Object.keys(filtersCount).includes(option.value)
                )}
                SelectedValue={filterOptions.find(
                  (option) => option.value === filter
                )}
                onChange={(selectedOption) => {
                  if (selectedOption) {
                    setFiltersCount((prevFilters) => {
                      const newFilters = { ...prevFilters } as Record<string, any>;
                      delete newFilters[filter];
                      newFilters[selectedOption.value] = undefined;
                      return newFilters as T;
                    });
                  }
                }}
                className="min-w-123 w-auto max-h-30"
              />

              <TextInput
                customInputStyles=" w-full h-30"
                onChange={(e) => {
                  const newFilters = {
                    ...filtersCount
                  } as Record<string, any>;
                  newFilters[filter] = e.target.value;
                  setFiltersCount(newFilters as T);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && onSearch) {
                    onSearch();
                  }
                }}
                value={value as string}
                placeholder="Containing"
              />
            </div>
          );
        })}
      </div>

      <div className="flex gap-10">
        {Object.keys(filtersCount).length > 0 && (
          <>
            <Button
              style={{
                maxHeight: 50,
                padding: '0 1.6rem',
                whiteSpace: 'nowrap',
              }}
              type="secondary"
              onClick={onSearch}
              border
            >
              Search
            </Button>

            <Button
              style={{
                maxHeight: 50,
                padding: '0 1.6rem',
                whiteSpace: 'nowrap',
              }}
              type="secondary"
              onClick={() => {
                setFiltersCount({} as T);
              }}
              border
            >
              Clear All
            </Button>
          </>
        )}
      </div>
    </div>
  );
};
